import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { UserService } from '../services/user.service';
import { CustomerAccountOperationComponent } from './customer-account-operation.component';
import {
  CustomerAccountHistoryFilter,
  CustomerAccountKind,
  CustomerAccountOperation,
  CustomerAccountOrganization,
  CustomerAccountService,
  CustomerAccountSummary
} from './customer-account.service';
import {
  accountBlockedMessage,
  accountDate,
  accountError,
  accountKindLabel,
  accountStatusLabel,
  downloadAccountBlob
} from './customer-account.utils';

@Component({
  selector: 'app-customer-account',
  templateUrl: './customer-account.component.html',
  styleUrls: ['./customer-account.scss']
})
export class CustomerAccountComponent implements OnInit, OnChanges, OnDestroy {
  @Input() clientId = 0;
  @Input() clientName = '';
  @Input() compact = false;
  @Input() orgId = 0;
  @Output() orgIdChange = new EventEmitter<number>();
  organizations: CustomerAccountOrganization[] = [];
  summary?: CustomerAccountSummary;
  overview?: any;
  history: CustomerAccountOperation[] = [];
  historyLength = 0;
  openingBalance: number | null = null;
  closingBalance: number | null = null;
  loading = false;
  historyLoading = false;
  exporting = false;
  error = '';
  historyError = '';
  isAdmin = false;
  dateFrom = new FormControl(null);
  dateTo = new FormControl(null);
  kind = '';
  status = '';
  filtersOpened = false;
  sort = 'created_at';
  direction: 'asc' | 'desc' = 'desc';
  invoiceFilter = '';
  actorFilter = '';
  expanded = new Set<number>();
  detailsById = new Map<number, any>();
  private subscription = new Subscription();
  private revision = 0;
  private historyRevision = 0;
  private initialized = false;
  private appliedHistoryFilter?: CustomerAccountHistoryFilter;
  readonly kindLabel = accountKindLabel;
  readonly statusLabel = accountStatusLabel;
  readonly kinds = [
    'TOPUP',
    'ALLOCATE',
    'PAYOUT',
    'RETURN_CREDIT',
    'DOCUMENT_CHANGE',
    'CONFIRM_BANK',
    'CANCEL_PENDING',
    'REVERSE',
    'OPENING'
  ];
  readonly statuses = ['SUCCESS', 'PENDING', 'FAILED', 'CANCELLED'];
  get activeOrgName() {
    return this.organizations.find((org) => org.id === this.orgId)?.name || '';
  }
  get blocked() {
    return !this.summary?.enabled || !this.summary?.reconciled;
  }
  get blockedReason() {
    return (
      accountBlockedMessage(this.summary?.blocked_reason) ||
      (!this.summary?.enabled
        ? 'Операции по балансу для этой организации ещё не включены.'
        : !this.summary?.reconciled
        ? 'Начальные остатки организации ещё не сверены.'
        : '')
    );
  }
  get hasMore() {
    return this.history.length < this.historyLength;
  }
  constructor(
    private accounts: CustomerAccountService,
    private dialog: MatDialog,
    private users: UserService
  ) {}
  async ngOnInit() {
    this.initialized = true;
    this.subscription.add(
      this.accounts.changed.subscribe((scope) => {
        if (scope.clientId === this.clientId && scope.orgId === this.orgId) void this.reload();
      })
    );
    this.subscription.add(
      this.users.user.subscribe(
        (user) =>
          (this.isAdmin = Boolean(
            user?.roles?.some((role) =>
              [role.description, role.normalize_description].some(
                (name) =>
                  String(name || '')
                    .trim()
                    .toLowerCase() === 'admin'
              )
            )
          ))
      )
    );
    try {
      this.organizations = (await firstValueFrom(this.accounts.organizations())).data || [];
      if (!this.orgId && this.organizations.length) {
        this.orgId = this.organizations[0].id;
        this.orgIdChange.emit(this.orgId);
      }
      await this.reload();
    } catch (error) {
      this.error = accountError(error, 'Не удалось загрузить организации взаиморасчётов');
    }
  }
  ngOnChanges(changes: SimpleChanges) {
    if (this.initialized && (changes.clientId || changes.orgId)) void this.reload();
  }
  ngOnDestroy() {
    this.revision++;
    this.historyRevision++;
    this.subscription.unsubscribe();
  }
  selectOrg(value: number) {
    this.orgId = value;
    this.orgIdChange.emit(value);
    void this.reload();
  }
  async reload() {
    this.overview = undefined;
    const revision = ++this.revision;
    this.historyRevision++;
    this.summary = undefined;
    this.history = [];
    this.historyLength = 0;
    this.error = '';
    this.historyError = '';
    this.appliedHistoryFilter = undefined;
    if (!this.clientId || !this.orgId) return;
    this.loading = true;
    try {
      const summary = await firstValueFrom(this.accounts.summary(this.clientId, this.orgId));
      if (revision !== this.revision) return;
      this.summary = summary;
      if (!this.compact) await this.loadHistory(true);
    } catch (error) {
      if (revision === this.revision)
        this.error = accountError(error, 'Не удалось загрузить баланс клиента');
    } finally {
      if (revision === this.revision) this.loading = false;
    }
  }
  async showOverview() {
    if (this.overview) {
      await this.reload();
      return;
    }
    this.loading = true;
    this.error = '';
    try {
      this.overview = await firstValueFrom(this.accounts.overview(this.clientId));
    } catch (error) {
      this.error = accountError(error, 'Не удалось загрузить остатки по организациям');
    } finally {
      this.loading = false;
    }
  }
  filter(): CustomerAccountHistoryFilter {
    return {
      client_id: this.clientId,
      org_id: this.orgId,
      date_from: accountDate(this.dateFrom.value),
      date_to: accountDate(this.dateTo.value),
      kind: this.kind || undefined,
      status: this.status || undefined,
      sort: this.sort,
      direction: this.direction,
      invoice_id: Number(this.invoiceFilter) || undefined,
      actor_user_id: Number(this.actorFilter) || undefined
    };
  }
  async loadHistory(reset = false) {
    if (this.historyLoading && !reset) return;
    if (!this.orgId || !this.clientId) return;
    const filter = reset || !this.appliedHistoryFilter ? this.filter() : this.appliedHistoryFilter;
    if (filter.date_from && filter.date_to && filter.date_from > filter.date_to) {
      this.historyError = 'Конец периода должен быть не раньше начала';
      return;
    }
    const revision = reset ? ++this.historyRevision : this.historyRevision;
    if (reset) {
      this.history = [];
      this.appliedHistoryFilter = filter;
    }
    this.historyLoading = true;
    this.historyError = '';
    try {
      const response = await firstValueFrom(
        this.accounts.history({ ...filter, limit: 30, offset: this.history.length })
      );
      if (revision !== this.historyRevision) return;
      if (response.as_of) this.appliedHistoryFilter = { ...filter, as_of: response.as_of };
      const known = new Set(this.history.map((operation) => operation.id));
      this.history = [
        ...this.history,
        ...(response.data || []).filter((operation) => !known.has(operation.id))
      ];
      this.historyLength = Number(response.length || 0);
      this.openingBalance =
        response.opening_balance == null ? null : Number(response.opening_balance);
      this.closingBalance =
        response.closing_balance == null ? null : Number(response.closing_balance);
    } catch (error) {
      if (revision === this.historyRevision)
        this.historyError = accountError(error, 'Не удалось загрузить операции');
    } finally {
      if (revision === this.historyRevision) this.historyLoading = false;
    }
  }
  edge(event: Event) {
    const element = event.target as HTMLElement;
    if (
      this.hasMore &&
      !this.historyLoading &&
      !this.historyError &&
      element.scrollHeight - element.scrollTop - element.clientHeight < 150
    )
      void this.loadHistory();
  }
  async export(format: 'pdf' | 'csv') {
    if (this.exporting || !this.summary) return;
    this.exporting = true;
    this.historyError = '';
    try {
      downloadAccountBlob(
        await firstValueFrom(
          this.accounts.exportHistory(this.appliedHistoryFilter || this.filter(), format)
        ),
        `client-${this.clientId}-org-${this.orgId}-settlement.${format}`
      );
    } catch (error) {
      this.historyError = accountError(error, 'Не удалось выгрузить акт сверки');
    } finally {
      this.exporting = false;
    }
  }
  action(kind: CustomerAccountKind, sourceOperation?: CustomerAccountOperation) {
    if (!this.summary || (this.blocked && kind !== 'CANCEL_PENDING')) return;
    this.dialog.open(CustomerAccountOperationComponent, {
      width: '760px',
      maxWidth: 'calc(100vw - 24px)',
      maxHeight: 'calc(100dvh - 24px)',
      autoFocus: false,
      data: {
        clientId: this.clientId,
        clientName: this.clientName,
        orgId: this.orgId,
        orgName: this.activeOrgName,
        kind,
        summary: this.summary,
        sourceOperation
      }
    });
  }
  isPendingBank(operation: CustomerAccountOperation) {
    return (
      ['TOPUP', 'PAYOUT'].includes(operation.kind) &&
      operation.channel === 'BANK' &&
      ['PENDING', 'PENDING_BANK', 'PENDING_CONFIRMATION'].includes(operation.status)
    );
  }
  async details(row: CustomerAccountOperation) {
    if (this.expanded.has(row.id)) {
      this.expanded.delete(row.id);
      return;
    }
    this.expanded.add(row.id);
    try {
      this.detailsById.set(row.id, await firstValueFrom(this.accounts.operation(row.id)));
    } catch (error) {
      this.historyError = accountError(error, 'Не удалось загрузить связи операции');
    }
  }
  async retryReceipt(row: CustomerAccountOperation) {
    try {
      await firstValueFrom(this.accounts.retryReceipt(row.id));
      await this.loadHistory(true);
    } catch (error) {
      this.historyError = accountError(error, 'Не удалось повторить чек');
    }
  }
}
